// Extraído de PeticionDetail.jsx (refactor mecánico, sin cambios funcionales)
import React, { useEffect, useState } from "react";
import api, { TIPO_LINEA, ESTADO_COMPONENTE, ORIGEN_LABELS, ORIGENES_ACTIVOS, PLANTILLA_LABELS } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SelectorArticuloProduccion from "@/components/SelectorArticuloProduccion";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Euro, Image as ImageIcon } from "lucide-react";
import CalcMaterialPanel from "@/components/CalcMaterialPanel";

// ============ NUEVO COMPONENTE DIALOG ============
export function NuevoComponenteDialog({ open, setOpen, lineas, espacios, materiales, proveedores, onCreated, onMaterialesChanged, editing, onClose, wizard = null, hideTrigger = false }) {
  const isEdit = !!editing;
  const [wizardAccion, setWizardAccion] = useState("terminar"); // otro | terminar (movido dentro del diálogo correcto)
  const [form, setForm] = useState({
    linea_id: "", nombre: "", descripcion: "", descripcion_encargo: "",
    tipo_plantilla: "libre", material_id: "", articulo: null, proveedor_id: "",
    cantidad: 1, ancho_cm: "", alto_cm: "", tamano: "",
    coste_unitario: 0, precio_unitario: 0,
    // Iter 73 — overrides editables in-situ del €/m² del material (bug Fernando):
    // Por defecto siguen al material seleccionado; el usuario puede tunearlos
    // para esta petición SIN afectar el catálogo.
    precio_m2_override: "", coste_m2_override: "",
    merma_pct_extra: 0,  // Iter 60.11
    estado: "pendiente", origen: "produccion_propia",
    imagen_url: "", notas: "",
    aaff_recibido: false,
    // marco_vinilo params
    marco_ancho_escaparate: "", marco_alto_escaparate: "",
    marco_ancho_cabecera: 25, marco_ancho_pie: 15, marco_ancho_lateral: 10,
  });
  const [saving, setSaving] = useState(false);
  // Iter 60 — Crear material on-the-fly (inline) sin salir del componente
  const [nuevoMatOpen, setNuevoMatOpen] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        linea_id: editing.linea_id || "", nombre: editing.nombre || "",
        descripcion: editing.descripcion || "", descripcion_encargo: editing.descripcion_encargo || "",
        tipo_plantilla: editing.tipo_plantilla || "libre",
        material_id: editing.material_id || "", proveedor_id: editing.proveedor_id || "",
        articulo: editing.articulo_ref ? { ref: editing.articulo_ref, fuente: editing.articulo_fuente, fuente_id: editing.articulo_ref.split(":")[1], material_id: editing.material_id || null, ...(editing.articulo_snapshot || {}) } : (editing.material_id ? { ref: `habitual:${editing.material_id}`, fuente: "habitual", fuente_id: editing.material_id, material_id: editing.material_id, nombre: (materiales.find((m) => m.id === editing.material_id) || {}).nombre, unidad: (materiales.find((m) => m.id === editing.material_id) || {}).unidad } : null),
        cantidad: editing.cantidad ?? 1,
        ancho_cm: editing.ancho_cm ?? "", alto_cm: editing.alto_cm ?? "",
        tamano: editing.tamano || "",
        coste_unitario: editing.coste_unitario ?? 0, precio_unitario: editing.precio_unitario ?? 0,
        precio_m2_override: "", coste_m2_override: "",  // Iter 73 - se autorrellenan al seleccionar material
        merma_pct_extra: editing.merma_pct_extra ?? 0,  // Iter 60.11 — merma manual extra
        estado: editing.estado || "pendiente",
        origen: editing.origen || "produccion_propia",
        imagen_url: editing.imagen_url || "", notas: editing.notas || "",
        aaff_recibido: !!editing.aaff_recibido,
        referencia_cliente: editing.referencia_cliente || "",
        // Iter 124 · cargar marco_params persistidos para poder editarlos sin recrear el marco.
        marco_ancho_escaparate: editing.marco_params?.ancho_escaparate ?? "",
        marco_alto_escaparate: editing.marco_params?.alto_escaparate ?? "",
        marco_ancho_cabecera: editing.marco_params?.ancho_cabecera ?? 25,
        marco_ancho_pie: editing.marco_params?.ancho_pie ?? 15,
        marco_ancho_lateral: editing.marco_params?.ancho_lateral ?? 10,
      });
    } else if (open) {
      setForm({
        // Wizard "Definir producción": linea_id fijo + referencia cliente heredada (corregible)
        linea_id: wizard?.linea?.id || "", nombre: "", descripcion: "", descripcion_encargo: "",
        tipo_plantilla: "libre", material_id: "", articulo: null, proveedor_id: "",
        cantidad: 1, ancho_cm: "", alto_cm: "", tamano: "",
        coste_unitario: 0, precio_unitario: 0,
        precio_m2_override: "", coste_m2_override: "",  // Iter 73
        merma_pct_extra: 0,
        estado: "pendiente", origen: "produccion_propia",
        imagen_url: "", notas: "", aaff_recibido: false,
        referencia_cliente: wizard?.linea?.referencia_cliente || "",
        marco_ancho_escaparate: "", marco_alto_escaparate: "",
        marco_ancho_cabecera: 25, marco_ancho_pie: 15, marco_ancho_lateral: 10,
      });
    }
  }, [editing, open]);

  // Jul-2026 (tabla Fernando): origen por defecto según el tipo de la línea
  // seleccionada — una retirada ya está en el PDV y no debe pedir AAFF.
  useEffect(() => {
    if (isEdit || !form.linea_id) return;
    const ln = (lineas || []).find((l) => l.id === form.linea_id);
    if (!ln) return;
    const tipos = new Set([...(ln.tipos || []), ln.tipo].filter(Boolean));
    let def = "produccion_propia";
    if (tipos.has("retirada")) def = "ya_en_pdv";
    else if (tipos.has("solo_montaje")) def = "cliente_a_pdv";
    setForm((f) => (f.origen === def ? f : { ...f, origen: def }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.linea_id]);

  const m2 = form.ancho_cm && form.alto_cm
    ? (parseFloat(form.ancho_cm) * parseFloat(form.alto_cm)) / 10000
    : null;
  // Iter 60.11 — m² FINAL aplicando merma extra (display + cálculo coste/precio)
  const mermaExtra = parseFloat(form.merma_pct_extra) || 0;
  const m2Final = m2 ? +(m2 * (1 + mermaExtra / 100)).toFixed(3) : null;

  // Auto-rellenar medidas desde la zona del espacio cuando se selecciona la línea
  // (solo si ancho/alto están vacíos para no sobrescribir lo que el usuario haya tecleado)
  React.useEffect(() => {
    if (!form.linea_id || form.ancho_cm || form.alto_cm) return;
    const linea = lineas.find((l) => l.id === form.linea_id);
    if (!linea?.espacio_id || !linea?.zona_id) return;
    const esp = espacios.find((e) => e.id === linea.espacio_id);
    const zona = esp?.zonas?.find((z) => z.id === linea.zona_id);
    if (zona && (zona.ancho_cm || zona.alto_cm)) {
      setForm((f) => ({
        ...f,
        ancho_cm: zona.ancho_cm || f.ancho_cm,
        alto_cm: zona.alto_cm || f.alto_cm,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.linea_id]);

  // Iter 124 · Marco de vinilo: al elegir plantilla, auto-rellenar las medidas del escaparate
  // desde la zona del espacio de la línea. Ahorra al usuario re-tipear ancho/alto del escaparate.
  useEffect(() => {
    if (isEdit) return;
    if (form.tipo_plantilla !== "marco_vinilo") return;
    if (form.marco_ancho_escaparate || form.marco_alto_escaparate) return;
    if (!form.linea_id) return;
    const linea = lineas.find((l) => l.id === form.linea_id);
    if (!linea?.espacio_id) return;
    const esp = espacios.find((e) => e.id === linea.espacio_id);
    // 1) preferimos la zona seleccionada
    let zona = null;
    if (linea.zona_id) zona = esp?.zonas?.find((z) => z.id === linea.zona_id);
    // 2) fallback: zona que se llame algo tipo "escaparate"
    if (!zona) zona = esp?.zonas?.find((z) => /escap/i.test(z.nombre || ""));
    // 3) fallback final: primera zona con medidas
    if (!zona) zona = esp?.zonas?.find((z) => z.ancho_cm || z.alto_cm);
    if (zona && (zona.ancho_cm || zona.alto_cm)) {
      setForm((f) => ({
        ...f,
        marco_ancho_escaparate: zona.ancho_cm ? String(zona.ancho_cm) : f.marco_ancho_escaparate,
        marco_alto_escaparate: zona.alto_cm ? String(zona.alto_cm) : f.marco_alto_escaparate,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.tipo_plantilla, form.linea_id]);

  // CAMPOS CONDICIONALES según los TIPOS de la línea seleccionada.
  // Lógica negocio (DNF):
  //  - tipos válidos: diseno, produccion, envio, montaje, desmontaje, retirada, otros
  //  - Si la línea tiene PRODUCCIÓN → origen = "produccion_propia" (fijo, sin preguntar)
  //  - Si la línea tiene MONTAJE pero NO producción → PREGUNTAR origen (4 opciones, sin produccion_propia)
  //  - Si la línea solo tiene retirada/desmontaje/envio/otros → no hay objeto físico, ocultar todo
  const lineaSel = lineas.find((l) => l.id === form.linea_id);
  const tiposLinea = Array.isArray(lineaSel?.tipos) && lineaSel.tipos.length > 0
    ? lineaSel.tipos
    : (lineaSel?.tipo ? [lineaSel.tipo] : []);
  const tieneProduccion = tiposLinea.some((t) =>
    ["produccion", "produccion_montaje", "diseno_produccion_montaje", "diseno_produccion"].includes(t)
  );
  const tieneDiseno = tiposLinea.some((t) =>
    ["diseno", "diseno_produccion_montaje", "diseno_produccion"].includes(t)
  );
  const tieneMontaje = tiposLinea.some((t) =>
    ["montaje", "solo_montaje", "produccion_montaje", "diseno_produccion_montaje"].includes(t)
  );
  // ¿La línea representa solo trabajos sin objeto físico (retirada, desmontaje, envío suelto, otros)?
  const sinObjetoFisico = !tieneProduccion && !tieneMontaje && !tieneDiseno && tiposLinea.length > 0;
  // ¿Pedimos origen al usuario? Solo si hay montaje sin producción.
  const muestraOrigen = tieneMontaje && !tieneProduccion;
  // ¿Mostramos material/proveedor/medidas/precio?
  // - Siempre que haya producción (DNF lo fabrica) o montaje (con material aportado).
  const muestraMaterialProveedor = tieneProduccion || tieneMontaje;
  // ¿Mostramos AAFF? Solo si DNF produce (no si el material lo trae un proveedor).
  const muestraAaff = tieneProduccion;
  // Opciones de origen permitidas:
  // Iter 60.11 — Permitimos SIEMPRE todos los orígenes para poder mezclar
  // en una misma línea componentes que fabricamos nosotros (produccion_propia)
  // con componentes que envía el cliente/proveedor (proveedor_a_nave / cliente_a_nave).
  // Caso típico: marco de vinilo (propio) + pancarta (cliente) en la misma línea.
  const origenesValidos = ORIGENES_ACTIVOS.map((k) => [k, ORIGEN_LABELS[k]]);

  // Auto-corrección al cambiar de línea: sugiere origen por defecto según tipos.
  // Pero NO fuerza el cambio si el usuario ya seleccionó otro origen válido.
  React.useEffect(() => {
    if (!form.linea_id) return;
    // Sugerencia inicial: si hay producción → produccion_propia; si solo montaje → proveedor_a_nave
    // Solo lo aplica si origen está vacío o es el default contrario al esperado por la línea
    if (tieneProduccion && !form.origen) {
      setForm((f) => ({ ...f, origen: "produccion_propia" }));
    } else if (!tieneProduccion && form.origen === "produccion_propia") {
      setForm((f) => ({ ...f, origen: "proveedor_a_nave" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.linea_id, tieneProduccion]);

  // Auto-recalcular coste/precio cuando hay material seleccionado + medidas
  // precio_unitario = precio_venta_material × m² (asumiendo material por m²)
  // Para marco_vinilo se usa el m² REAL del marco (suma sub-piezas), no el escaparate.
  // NOTA: este useEffect se declara más abajo (después de marcoTotalM2) para evitar TDZ.

  // Material "activo" para cálculos: habitual real o artículo de catálogo (snapshot) — misma lógica para ambas fuentes
  const matActivo = form.material_id
    ? materiales.find((m) => m.id === form.material_id)
    : (form.articulo?.fuente === "catalogo" ? { id: form.articulo.ref, nombre: form.articulo.nombre, unidad: form.articulo.unidad, precio_venta: form.articulo.precio_venta || 0, precio_coste: form.articulo.precio_coste || 0, merma_pct: 0 } : null);
  // Selector unificado: item = {ref, fuente: habitual|catalogo, fuente_id, material_id, nombre, unidad, categoria, precio_coste, precio_venta, merma_pct, proveedor_id}
  const selectArticulo = (it) => {
    const snapshot = { nombre: it.nombre, unidad: it.unidad, categoria: it.categoria || null, precio_coste: it.precio_coste ?? null, precio_venta: it.precio_venta ?? null, capturado_at: new Date().toISOString() };
    const articulo = { ...it, ...snapshot };
    if (it.fuente === "habitual") {
      selectMaterial(it.material_id, articulo);
      return;
    }
    const factor = (it.unidad === "m²" && m2 && m2 > 0) ? m2 : 1;
    setForm({
      ...form, material_id: "", articulo,
      coste_unitario: +((it.precio_coste || 0) * factor).toFixed(2),
      precio_unitario: +((it.precio_venta || 0) * factor).toFixed(2),
      precio_m2_override: it.unidad === "m²" ? String(it.precio_venta ?? "") : "",
      coste_m2_override: it.unidad === "m²" ? String(it.precio_coste ?? "") : "",
    });
  };
  const selectMaterial = (mid, articulo = null) => {
    const mat = materiales.find((m) => m.id === mid);
    if (!articulo && mat) articulo = { ref: `habitual:${mat.id}`, fuente: "habitual", fuente_id: mat.id, material_id: mat.id, nombre: mat.nombre, unidad: mat.unidad, categoria: mat.categoria || null, precio_coste: mat.precio_coste ?? null, precio_venta: mat.precio_venta ?? null, capturado_at: new Date().toISOString() };
    if (mat) {
      const factor = (mat.unidad === "m²" && m2 && m2 > 0) ? m2 : 1;
      const mermaFactor = 1 + ((mat.merma_pct || 0) / 100);
      // Iter 73 — sembrar overrides editables con los valores del catálogo.
      // Si el material no es por m² dejamos overrides vacíos (no aplica).
      const seedPrecioM2 = mat.unidad === "m²" ? (mat.precio_venta ?? "") : "";
      const seedCosteM2 = mat.unidad === "m²" ? (mat.precio_coste ?? "") : "";
      setForm({
        ...form, material_id: mid, articulo,
        coste_unitario: +((mat.precio_coste || 0) * factor * mermaFactor).toFixed(2),
        precio_unitario: +((mat.precio_venta || 0) * factor).toFixed(2),
        precio_m2_override: String(seedPrecioM2),
        coste_m2_override: String(seedCosteM2),
        proveedor_id: mat.proveedor_id || form.proveedor_id,
      });
    } else setForm({ ...form, material_id: mid, articulo });
  };

  const selectPlantilla = (tp) => {
    const labels = { marco_vinilo: "Marco de vinilo", podium_1x1: "Podium 1×1", pancarta: "Pancarta", libre: "" };
    setForm((f) => ({ ...f, tipo_plantilla: tp, nombre: f.nombre || labels[tp] || "" }));
  };

  const [uploading, setUploading] = useState(false);
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10_000_000) { alert("Imagen demasiado grande (máx 10 MB)."); return; }
    setUploading(true);
    try {
      const { uploadToCloudinary } = await import("@/lib/cloudinary");
      const { secure_url } = await uploadToCloudinary(file, "dnf/componentes");
      setForm((f) => ({ ...f, imagen_url: secure_url }));
    } catch (err) {
      alert("Error al subir imagen: " + (err.message || err));
    } finally { setUploading(false); }
  };

  const handleClose = () => {
    setOpen(false);
    if (onClose) onClose();
  };

  // Cálculo en vivo de las sub-piezas para preview del marco
  const marcoSubs = (() => {
    if (form.tipo_plantilla !== "marco_vinilo") return [];
    const aw = parseFloat(form.marco_ancho_escaparate);
    const ah = parseFloat(form.marco_alto_escaparate);
    const cab = parseFloat(form.marco_ancho_cabecera) || 0;
    const pie = parseFloat(form.marco_ancho_pie) || 0;
    const lat = parseFloat(form.marco_ancho_lateral) || 0;
    if (!aw || !ah) return [];
    const latAlto = Math.max(ah - cab - pie, 0);
    return [
      { nombre: "Cabecera", w: aw, h: cab },
      { nombre: "Pie", w: aw, h: pie },
      { nombre: "Lateral izquierdo", w: lat, h: latAlto },
      { nombre: "Lateral derecho", w: lat, h: latAlto },
    ].map((s) => ({ ...s, m2: (s.w * s.h) / 10000 }));
  })();

  // m² REAL de vinilo del marco (suma de las 4 piezas) — para coste/precio correctos.
  // Se aplica `merma_pct_extra` como margen adicional configurable por el usuario.
  const marcoTotalM2 = marcoSubs.reduce((acc, s) => acc + (s.m2 || 0), 0);
  const marcoTotalM2Final = marcoTotalM2 * (1 + (mermaExtra || 0) / 100);

  // Auto-recalcular coste/precio cuando hay material seleccionado + medidas
  // precio_unitario = precio_m2_override (o catálogo) × m² (asumiendo material por m²)
  // Para marco_vinilo se usa el m² REAL del marco (suma sub-piezas), no el escaparate.
  React.useEffect(() => {
    if (!matActivo) return;
    const mat = matActivo;
    if (!mat) return;
    const m2Real = form.tipo_plantilla === "marco_vinilo" ? marcoTotalM2Final : m2Final;
    const factor = (mat.unidad === "m²" && m2Real && m2Real > 0) ? m2Real : 1;
    const mermaFactor = 1 + ((mat.merma_pct || 0) / 100);
    // Iter 73 — Si el usuario ha sobrescrito el €/m² in-situ, usa ese valor.
    const precioM2 = (form.precio_m2_override !== "" && !isNaN(parseFloat(form.precio_m2_override)))
      ? parseFloat(form.precio_m2_override)
      : (mat.precio_venta || 0);
    const costeM2 = (form.coste_m2_override !== "" && !isNaN(parseFloat(form.coste_m2_override)))
      ? parseFloat(form.coste_m2_override)
      : (mat.precio_coste || 0);
    const newCoste = +(costeM2 * factor * mermaFactor).toFixed(2);
    const newPrecio = +(precioM2 * factor).toFixed(2);
    setForm((f) => {
      if (parseFloat(f.coste_unitario) === newCoste && parseFloat(f.precio_unitario) === newPrecio) return f;
      return { ...f, coste_unitario: newCoste, precio_unitario: newPrecio };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.material_id, form.articulo, form.ancho_cm, form.alto_cm, form.merma_pct_extra, marcoTotalM2Final, form.tipo_plantilla, form.precio_m2_override, form.coste_m2_override]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre,
        descripcion: form.descripcion || null,
        descripcion_encargo: form.descripcion_encargo || null,
        tipo_plantilla: form.tipo_plantilla,
        material_id: form.material_id || null,
        articulo_fuente: form.articulo?.fuente || null,
        articulo_ref: form.articulo?.ref || null,
        articulo_snapshot: form.articulo ? { nombre: form.articulo.nombre, unidad: form.articulo.unidad, categoria: form.articulo.categoria || null, precio_coste: form.articulo.precio_coste ?? null, precio_venta: form.articulo.precio_venta ?? null, capturado_at: form.articulo.capturado_at || new Date().toISOString() } : null,
        proveedor_id: form.proveedor_id || null,
        cantidad: parseFloat(form.cantidad) || 1,
        ancho_cm: form.ancho_cm ? parseFloat(form.ancho_cm) : null,
        alto_cm: form.alto_cm ? parseFloat(form.alto_cm) : null,
        metros_cuadrados: m2Final ?? m2,
        merma_pct_extra: mermaExtra,
        tamano: form.tamano || null,
        coste_unitario: parseFloat(form.coste_unitario) || 0,
        precio_unitario: parseFloat(form.precio_unitario) || 0,
        estado: form.estado,
        origen: form.origen,
        imagen_url: form.imagen_url || null,
        notas: form.notas || null,
        aaff_recibido: !!form.aaff_recibido,
        referencia_cliente: (form.referencia_cliente && String(form.referencia_cliente).trim()) || null,
      };
      if (isEdit) {
        // BUG FIX (Iter 124): al editar un marco_vinilo NO enviamos los campos calculados
        // — o el backend los recalcula desde marco_params, o mantiene los originales.
        if (form.tipo_plantilla === "marco_vinilo") {
          delete payload.metros_cuadrados;
          delete payload.coste_unitario;
          delete payload.precio_unitario;
          delete payload.ancho_cm;
          delete payload.alto_cm;
          delete payload.merma_pct_extra;
          // Si el usuario ha modificado los params del marco, los enviamos.
          // El backend detectará el cambio y recalculará m²/precio/coste + regenerará sub-piezas.
          if (form.marco_ancho_escaparate && form.marco_alto_escaparate) {
            const orig = editing.marco_params || {};
            const nuevos = {
              ancho_escaparate: parseFloat(form.marco_ancho_escaparate),
              alto_escaparate: parseFloat(form.marco_alto_escaparate),
              ancho_cabecera: parseFloat(form.marco_ancho_cabecera) || 25,
              ancho_pie: parseFloat(form.marco_ancho_pie) || 15,
              ancho_lateral: parseFloat(form.marco_ancho_lateral) || 10,
            };
            const cambio = Object.keys(nuevos).some((k) => Number(orig[k] ?? -1) !== nuevos[k]);
            if (cambio) payload.marco_params = nuevos;
          }
        }
        await api.patch(`/componentes/${editing.id}`, payload);
      } else {
        payload.linea_id = form.linea_id;
        // marco params para creación
        if (form.tipo_plantilla === "marco_vinilo" && form.marco_ancho_escaparate && form.marco_alto_escaparate) {
          payload.marco_params = {
            ancho_escaparate: parseFloat(form.marco_ancho_escaparate),
            alto_escaparate: parseFloat(form.marco_alto_escaparate),
            ancho_cabecera: parseFloat(form.marco_ancho_cabecera) || 25,
            ancho_pie: parseFloat(form.marco_ancho_pie) || 15,
            ancho_lateral: parseFloat(form.marco_ancho_lateral) || 10,
          };
          // Setear ancho/alto del padre = ancho/alto escaparate (medida visual del marco)
          payload.ancho_cm = parseFloat(form.marco_ancho_escaparate);
          payload.alto_cm = parseFloat(form.marco_alto_escaparate);
          // m² REAL del marco = suma de sub-piezas (backend recalcula igual; aquí coherencia visual)
          payload.metros_cuadrados = marcoTotalM2;
        }
        await api.post("/componentes", payload);
      }
      Object.keys(payload).forEach((k) => payload[k] === null && delete payload[k]);
      if (wizard && !isEdit) {
        wizard.onGuardado();
        onCreated();
        if (wizardAccion === "otro") {
          // mismo formulario, misma línea: reset de campos manteniendo linea_id y referencia
          setForm((f) => ({ ...f, nombre: "", descripcion: "", descripcion_encargo: "", material_id: "", articulo: null, proveedor_id: "", cantidad: 1,
            ancho_cm: "", alto_cm: "", tamano: "", coste_unitario: 0, precio_unitario: 0, precio_m2_override: "", coste_m2_override: "",
            merma_pct_extra: 0, imagen_url: "", notas: "", aaff_recibido: false, tipo_plantilla: "libre",
            marco_ancho_escaparate: "", marco_alto_escaparate: "" }));
          setWizardAccion("terminar");
          return;
        }
        wizard.onTerminar(1);
        setOpen(false);
        if (onClose) onClose();
        return;
      }
      handleClose();
      onCreated();
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else setOpen(v); }}>
      {!isEdit && !hideTrigger && (
        <DialogTrigger asChild>
          <Button data-testid="new-componente-btn" disabled={lineas.length === 0}
            className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Nuevo componente
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle data-testid="comp-dialog-title">{isEdit ? "Editar componente" : wizard ? `Definir producción · ${espacios.find((e) => e.id === wizard.linea?.espacio_id)?.nombre || "línea nueva"}` : "Nuevo componente físico"}</DialogTitle>
          {wizard && !isEdit && <p className="text-xs text-zinc-400">La línea ya está creada. Añade sus componentes de producción ahora o déjalo para más tarde.</p>}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {/* PLANTILLA (solo en crear) */}
          {!isEdit && (
            <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-3">
              <Label className="text-amber-300">Plantilla</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {Object.entries(PLANTILLA_LABELS).map(([k, v]) => (
                  <button key={k} type="button" onClick={() => selectPlantilla(k)}
                    data-testid={`plantilla-${k}`}
                    className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${
                      form.tipo_plantilla === k
                        ? "bg-amber-500/20 border-amber-500/60 text-amber-200"
                        : "bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-zinc-200"
                    }`}>{v}</button>
                ))}
              </div>
              {form.tipo_plantilla === "podium_1x1" && (
                <div className="text-[11px] text-amber-300/70 mt-2">ℹ️ Se crearán las 3 sub-piezas: Stocker (ya en PDV), Pancarta trasera (proveedor→nave), Suelo (producción propia)</div>
              )}
            </div>
          )}

          {/* MARCO VINILO - parámetros inteligentes (crear + editar, Iter 124) */}
          {form.tipo_plantilla === "marco_vinilo" && (
            <div className="bg-violet-500/5 border border-violet-500/30 rounded-lg p-3 space-y-3">
              <div className="text-violet-300 font-medium text-sm">📐 Medidas del marco de vinilo</div>
              <div className="text-xs text-zinc-400">
                {isEdit
                  ? "Modifica las medidas y guarda. Se recalcularán las 4 piezas y el m² automáticamente."
                  : "Define las medidas del escaparate y las anchuras de cabecera/pie/laterales. El sistema calculará las 4 piezas."}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Ancho escaparate (cm) *</Label>
                  <Input type="number" step="0.1" required value={form.marco_ancho_escaparate}
                    onChange={(e) => setForm({ ...form, marco_ancho_escaparate: e.target.value })}
                    placeholder="ej. 200"
                    data-testid="marco-ancho-escaparate"
                    className="bg-zinc-900 border-zinc-800" />
                </div>
                <div>
                  <Label className="text-xs">Alto escaparate (cm) *</Label>
                  <Input type="number" step="0.1" required value={form.marco_alto_escaparate}
                    onChange={(e) => setForm({ ...form, marco_alto_escaparate: e.target.value })}
                    placeholder="ej. 240"
                    data-testid="marco-alto-escaparate"
                    className="bg-zinc-900 border-zinc-800" />
                </div>
                <div>
                  <Label className="text-xs">Anchura cabecera (cm)</Label>
                  <Input type="number" step="0.1" value={form.marco_ancho_cabecera}
                    onChange={(e) => setForm({ ...form, marco_ancho_cabecera: e.target.value })}
                    className="bg-zinc-900 border-zinc-800" />
                </div>
                <div>
                  <Label className="text-xs">Anchura pie (cm)</Label>
                  <Input type="number" step="0.1" value={form.marco_ancho_pie}
                    onChange={(e) => setForm({ ...form, marco_ancho_pie: e.target.value })}
                    className="bg-zinc-900 border-zinc-800" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Anchura laterales (cm)</Label>
                  <Input type="number" step="0.1" value={form.marco_ancho_lateral}
                    onChange={(e) => setForm({ ...form, marco_ancho_lateral: e.target.value })}
                    className="bg-zinc-900 border-zinc-800" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Merma extra % <span className="text-zinc-500 font-normal">(margen adicional sobre la suma de las 4 piezas)</span></Label>
                  <Input type="number" step="1" min="0" max="100"
                    value={form.merma_pct_extra}
                    onChange={(e) => setForm({ ...form, merma_pct_extra: e.target.value })}
                    placeholder="ej. 10"
                    data-testid="marco-merma-extra"
                    className="bg-zinc-900 border-zinc-800" />
                </div>
              </div>
              {marcoSubs.length > 0 && (
                <div className="bg-zinc-950/60 rounded-lg p-2 border border-violet-500/20">
                  <div className="text-[10px] uppercase tracking-wider text-violet-300/80 mb-1.5">Sub-piezas que se crearán:</div>
                  <div className="space-y-1">
                    {marcoSubs.map((s, i) => (
                      <div key={i} className="text-xs text-zinc-300 flex justify-between items-center">
                        <span>↳ {s.nombre}</span>
                        <span className="flex items-center gap-3">
                          <span className="text-amber-300 font-mono">{s.w.toFixed(0)} × {s.h.toFixed(0)} cm</span>
                          <span className="text-emerald-300 font-mono text-[11px] w-16 text-right">{s.m2.toFixed(2)} m²</span>
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 pt-2 border-t border-violet-500/20 flex justify-between items-center text-xs">
                    <span className="text-violet-200 font-medium">📊 m² REAL de vinilo del marco:</span>
                    <span className="text-emerald-300 font-mono font-bold">{marcoTotalM2.toFixed(2)} m²</span>
                  </div>
                  {mermaExtra > 0 && (
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-amber-200/80">+ {mermaExtra}% merma extra (configurable):</span>
                      <span className="text-amber-300 font-mono font-bold">{marcoTotalM2Final.toFixed(2)} m²</span>
                    </div>
                  )}
                  {(() => {
                    const mat = matActivo;
                    if (!mat || mat.unidad !== "m²") return null;
                    const merma = mat.merma_pct || 0;
                    const m2ConMerma = marcoTotalM2Final * (1 + merma / 100);
                    const coste = m2ConMerma * (mat.precio_coste || 0);
                    const precio = m2ConMerma * (mat.precio_venta || 0);
                    return (
                      <div className="mt-2 pt-2 border-t border-violet-500/20 space-y-1 text-[11px]">
                        <div className="flex justify-between text-zinc-400">
                          <span>Coste total ({(mat.precio_coste || 0).toFixed(2)}€/m² × {m2ConMerma.toFixed(2)} m² {merma > 0 && `incl. ${merma}% merma material`}{mermaExtra > 0 && ` + ${mermaExtra}% extra`})</span>
                          <span className="text-zinc-200 font-mono">{coste.toFixed(2)}€</span>
                        </div>
                        <div className="flex justify-between text-emerald-200">
                          <span>💰 Precio venta ({(mat.precio_venta || 0).toFixed(2)}€/m²)</span>
                          <span className="font-mono font-bold">{precio.toFixed(2)}€</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            {!isEdit && (
              <div className="col-span-2">
                <Label>Línea de trabajo *</Label>
                <Select value={form.linea_id} onValueChange={(v) => setForm({ ...form, linea_id: v })} disabled={!!wizard && !isEdit}>
                  <SelectTrigger data-testid="form-comp-linea" className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="Selecciona línea" /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {lineas.map((l) => {
                      const esp = espacios.find((e) => e.id === l.espacio_id);
                      return <SelectItem key={l.id} value={l.id}>{(TIPO_LINEA[l.tipo]?.label || l.tipo)} — {esp?.nombre || "—"}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="col-span-2">
              <Label>Nombre *</Label>
              <Input data-testid="form-comp-nombre" required value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                className="bg-zinc-900 border-zinc-800" />
            </div>
            <div className="col-span-2">
              <Label>Descripción</Label>
              <Input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                className="bg-zinc-900 border-zinc-800" />
            </div>
            <div className="col-span-2">
              <Label>Descripción del encargo <span className="text-zinc-500 text-xs">(detalle de producción/proveedor)</span></Label>
              <Textarea value={form.descripcion_encargo} rows={2}
                onChange={(e) => setForm({ ...form, descripcion_encargo: e.target.value })}
                className="bg-zinc-900 border-zinc-800" />
            </div>

            <div className="col-span-2">
              <Label className="text-xs flex items-center gap-2">
                Ref. cliente <span className="text-[10px] text-zinc-500 normal-case">(opcional · si no se rellena, hereda la de la línea. Aparece en albarán y factura)</span>
              </Label>
              <Input value={form.referencia_cliente || ""}
                onChange={(e) => setForm({ ...form, referencia_cliente: e.target.value })}
                placeholder="P-2026/00123, OC-456…"
                data-testid="form-comp-referencia-cliente"
                className="bg-zinc-900 border-zinc-800" />
            </div>

            {/* AAFF — sólo si la línea contiene Producción (DNF fabrica) */}
            {muestraAaff && (
            <div className="col-span-2 bg-emerald-500/5 border border-emerald-500/30 rounded-lg p-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.aaff_recibido}
                  onChange={(e) => setForm({ ...form, aaff_recibido: e.target.checked })}
                  data-testid="form-comp-aaff"
                  className="accent-emerald-500 w-4 h-4" />
                <span className="text-sm text-emerald-200 font-medium">✓ Artes finales (AAFF) recibidos</span>
                <span className="text-xs text-zinc-500">— marcar cuando llegue el material gráfico del cliente</span>
              </label>
            </div>
            )}

            {/* ORIGEN — siempre visible si hay objeto físico (Iter 60.11)
                Permite mezclar produccion_propia + cliente_a_nave en la misma línea */}
            {(muestraMaterialProveedor || muestraOrigen) && (
            <div className="col-span-2 bg-blue-500/5 border border-blue-500/30 rounded-lg p-3">
              <Label className="text-blue-300">¿De dónde viene este componente?</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {origenesValidos.map(([k, v]) => (
                  <button key={k} type="button" onClick={() => setForm({ ...form, origen: k })}
                    data-testid={`origen-${k}`}
                    className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${
                      form.origen === k
                        ? "bg-blue-500/20 border-blue-500/60 text-blue-200"
                        : "bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-zinc-200"
                    }`}>{v.icon} {v.label}</button>
                ))}
              </div>
              <div className="text-[10px] text-zinc-500 mt-2">
                {form.origen === "produccion_propia"
                  ? "🏭 Se fabrica en DNF → entra al kanban de Producción (diseño/AAFF/fabricación)"
                  : "📦 NO entra a producción → solo recepción y montaje. Útil para mezclar componentes propios + del cliente."}
              </div>
            </div>
            )}
            {/* Aviso visible si la línea no tiene tipos coherentes */}
            {form.linea_id && sinObjetoFisico && (
              <div className="col-span-2 bg-amber-500/5 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-200">
                ℹ️ Esta línea solo incluye trabajos sin material gráfico (retirada / desmontaje / envío / otros).
                Puedes describir el elemento pero no necesitas material, proveedor ni origen.
                <div className="mt-1.5 text-emerald-300" data-testid="origen-inferido-badge">
                  📍 Origen asignado automáticamente: <span className="font-semibold">
                    {form.origen === "ya_en_pdv" ? "Ya en PDV (sin AAFF ni producción)" : form.origen === "cliente_a_pdv" ? "Cliente envía a PDV" : form.origen}
                  </span>
                </div>
              </div>
            )}

            {muestraMaterialProveedor && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>Material</Label>
                {onMaterialesChanged && (
                  <button type="button" onClick={() => setNuevoMatOpen(true)}
                    data-testid="inline-nuevo-material-btn"
                    className="text-[11px] text-amber-300 hover:text-amber-200 inline-flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Nuevo material
                  </button>
                )}
              </div>
              <SelectorArticuloProduccion value={form.articulo} onSelect={selectArticulo} onMaterialesChanged={onMaterialesChanged}
                onClear={() => setForm({ ...form, material_id: "", articulo: null })} />
            </div>
            )}
            {muestraMaterialProveedor && (
            <div>
              <Label>Proveedor</Label>
              <Select value={form.proveedor_id} onValueChange={(v) => setForm({ ...form, proveedor_id: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  {proveedores.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            )}
            {/* Iter 73 — Tarifa €/m² editable in-situ (sólo materiales por m²) */}
            {muestraMaterialProveedor && form.material_id && (() => {
              const mat = matActivo;
              if (!mat || mat.unidad !== "m²") return null;
              const catPV = mat.precio_venta || 0;
              const catPC = mat.precio_coste || 0;
              const pvNum = parseFloat(form.precio_m2_override);
              const pcNum = parseFloat(form.coste_m2_override);
              const pvDirty = !isNaN(pvNum) && Math.abs(pvNum - catPV) > 0.001;
              const pcDirty = !isNaN(pcNum) && Math.abs(pcNum - catPC) > 0.001;
              return (
                <div className="col-span-2 bg-amber-500/5 border border-amber-500/30 rounded-lg p-3">
                  <div className="text-amber-300 text-[11px] uppercase tracking-wider mb-2 font-bold flex items-center gap-2">
                    <Euro className="w-3 h-3" /> Tarifa €/m² — editable solo para esta petición
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Coste €/m² <span className="text-zinc-500">(catálogo {catPC.toFixed(2)} €)</span></Label>
                      <Input type="number" step="0.01" min="0"
                        value={form.coste_m2_override}
                        onChange={(e) => setForm({ ...form, coste_m2_override: e.target.value })}
                        data-testid="form-comp-coste-m2-override"
                        className={`bg-zinc-900 border-zinc-800 mt-1 font-mono ${pcDirty ? "text-amber-300 border-amber-500/50" : ""}`} />
                    </div>
                    <div>
                      <Label className="text-xs">Precio venta €/m² <span className="text-zinc-500">(catálogo {catPV.toFixed(2)} €)</span></Label>
                      <Input type="number" step="0.01" min="0"
                        value={form.precio_m2_override}
                        onChange={(e) => setForm({ ...form, precio_m2_override: e.target.value })}
                        data-testid="form-comp-precio-m2-override"
                        className={`bg-zinc-900 border-zinc-800 mt-1 font-mono ${pvDirty ? "text-amber-300 border-amber-500/50" : ""}`} />
                    </div>
                  </div>
                  {(pvDirty || pcDirty) && (
                    <div className="text-[11px] text-amber-200 mt-2 flex items-center gap-2">
                      <span>⚠ Tarifa modificada para este componente.</span>
                      <button type="button"
                        data-testid="form-comp-reset-tarifa"
                        onClick={() => setForm({ ...form, precio_m2_override: String(catPV), coste_m2_override: String(catPC) })}
                        className="underline hover:text-amber-100">
                        Restablecer al catálogo
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
            <div>
              <Label>Cantidad</Label>
              <Input type="number" step="0.01" value={form.cantidad}
                onChange={(e) => setForm({ ...form, cantidad: e.target.value })}
                className="bg-zinc-900 border-zinc-800" />
            </div>
            <div>
              <Label>Tamaño (texto)</Label>
              <Input placeholder='ej. "120cm", "A1"' value={form.tamano}
                onChange={(e) => setForm({ ...form, tamano: e.target.value })}
                className="bg-zinc-900 border-zinc-800" />
            </div>
            {(isEdit || form.tipo_plantilla !== "marco_vinilo") && (
              <>
                <div>
                  <Label>Ancho (cm)</Label>
                  <Input type="number" step="0.1" value={form.ancho_cm}
                    onChange={(e) => setForm({ ...form, ancho_cm: e.target.value })}
                    className="bg-zinc-900 border-zinc-800" />
                </div>
                <div>
                  <Label>Alto (cm)</Label>
                  <Input type="number" step="0.1" value={form.alto_cm}
                    onChange={(e) => setForm({ ...form, alto_cm: e.target.value })}
                    className="bg-zinc-900 border-zinc-800" />
                </div>
                <div>
                  <Label>m² base</Label>
                  <Input value={m2 != null ? m2.toFixed(2) : ""} disabled
                    data-testid="comp-m2-base"
                    className="bg-zinc-950 border-zinc-800 text-zinc-400" />
                </div>
                <div>
                  <Label>Merma extra %</Label>
                  <Input type="number" step="1" min="0" max="100"
                    value={form.merma_pct_extra}
                    onChange={(e) => setForm({ ...form, merma_pct_extra: e.target.value })}
                    placeholder="ej. 10"
                    data-testid="comp-merma-extra"
                    className="bg-zinc-900 border-zinc-800" />
                </div>
                <div>
                  <Label>m² FINAL (con merma)</Label>
                  <Input value={m2Final != null ? m2Final.toFixed(2) : ""} disabled
                    data-testid="comp-m2-final"
                    className="bg-zinc-950 border-zinc-800 text-amber-300 font-semibold" />
                </div>
                <div>
                  <Label>Estado</Label>
                  <Select value={form.estado} onValueChange={(v) => setForm({ ...form, estado: v })}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                      {Object.entries(ESTADO_COMPONENTE).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v.icon} {v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Iter 60.3 — Aviso semántico: entregado solo aplica a cliente_a_pdv */}
                  {form.estado === "entregado"
                    && form.origen
                    && form.origen !== "cliente_a_pdv"
                    && (
                    <div className="mt-1.5 text-[11px] bg-amber-500/[0.08] border border-amber-500/30 text-amber-200 rounded px-2 py-1.5 flex items-start gap-1.5"
                      data-testid="hint-entregado-warning">
                      <span className="text-amber-400">💡</span>
                      <span>
                        Para origen <strong>{form.origen.replace(/_/g, " ")}</strong>,
                        el estado típico es <code className="text-emerald-300">Recibido almacén</code>.
                        Usar <code className="text-amber-300">Entregado</code> está bien para tu flujo, pero
                        se considera semánticamente más preciso cuando el origen es <code>Cliente → PDV</code>.
                        <button type="button" onClick={() => setForm({ ...form, estado: "recibido_almacen" })}
                          data-testid="hint-fix-button"
                          className="ml-1 underline hover:text-amber-100">
                          Cambiar a "Recibido almacén"
                        </button>
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}
            <div>
              <Label>Coste unitario (€)</Label>
              <Input type="number" step="0.01" value={form.coste_unitario}
                onChange={(e) => setForm({ ...form, coste_unitario: e.target.value })}
                className="bg-zinc-900 border-zinc-800" />
              {form.material_id && (() => {
                const mat = matActivo;
                if (!mat) return null;
                const factor = (mat.unidad === "m²" && m2 && m2 > 0) ? m2 : (parseFloat(form.cantidad) || 1);
                const mermaPct = mat.merma_pct || 0;
                return (
                  <div className="text-[10px] text-zinc-500 mt-1 leading-tight">
                    📌 Catálogo: <b className="text-amber-400">{(mat.precio_coste || 0).toFixed(2)}€/{mat.unidad}</b>
                    {" × "}{factor.toFixed(2)}{mat.unidad === "m²" ? " m²" : " ud"}
                    {mermaPct > 0 && <> × <span className="text-amber-400">{(1 + mermaPct/100).toFixed(2)}</span> (merma {mermaPct}%)</>}
                  </div>
                );
              })()}
            </div>
            <div>
              <Label>Precio unitario (€)</Label>
              <Input type="number" step="0.01" value={form.precio_unitario}
                onChange={(e) => setForm({ ...form, precio_unitario: e.target.value })}
                className="bg-zinc-900 border-zinc-800" />
              {form.material_id && (() => {
                const mat = matActivo;
                if (!mat) return null;
                const factor = (mat.unidad === "m²" && m2 && m2 > 0) ? m2 : (parseFloat(form.cantidad) || 1);
                return (
                  <div className="text-[10px] text-emerald-400 mt-1 leading-tight">
                    💰 Catálogo: <b>{(mat.precio_venta || 0).toFixed(2)}€/{mat.unidad}</b>
                    {" × "}{factor.toFixed(2)}{mat.unidad === "m²" ? " m²" : " ud"}
                    {" = "}{((mat.precio_venta || 0) * factor).toFixed(2)}€
                    <span className="text-zinc-500"> · editable arriba ↑</span>
                  </div>
                );
              })()}
            </div>

            {/* CALCULADORA DE MATERIAL REAL */}
            <CalcMaterialPanel
              materialId={form.material_id}
              materiales={materiales}
              ancho_cm={form.ancho_cm}
              alto_cm={form.alto_cm}
              cantidad={form.cantidad}
              onApply={(out) => {
                setForm((f) => ({
                  ...f,
                  coste_unitario: out.coste_total,
                  precio_unitario: out.precio_total,
                }));
              }}
            />

            {/* IMAGEN */}
            <div className="col-span-2 bg-violet-500/5 border border-violet-500/30 rounded-lg p-3">
              <Label className="text-violet-300 flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> Imagen de referencia
              </Label>
              <div className="flex gap-3 mt-2 items-start">
                <input type="file" accept="image/*" onChange={handleImageUpload}
                  disabled={uploading}
                  data-testid="form-comp-imagen"
                  className="text-xs text-zinc-300 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-violet-500/20 file:text-violet-200 hover:file:bg-violet-500/30 disabled:opacity-50" />
                {uploading && <span className="text-xs text-violet-300">Subiendo a Cloudinary…</span>}
                {form.imagen_url && (
                  <div className="relative">
                    <img src={form.imagen_url} alt="ref" className="w-20 h-20 object-cover rounded border border-violet-500/50" />
                    <button type="button" onClick={() => setForm({ ...form, imagen_url: "" })}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs">×</button>
                  </div>
                )}
              </div>
              <div className="text-[11px] text-zinc-500 mt-1">Máx. 10 MB · alojado en Cloudinary con CDN.</div>
            </div>

            <div className="col-span-2">
              <Label>Notas</Label>
              <Textarea value={form.notas} rows={2}
                onChange={(e) => setForm({ ...form, notas: e.target.value })}
                className="bg-zinc-900 border-zinc-800" />
            </div>
          </div>
          {wizard && !isEdit ? (
            <DialogFooter className="flex-wrap gap-2" data-testid="wizard-produccion-footer">
              <div className="text-[11px] text-zinc-400 mr-auto self-center" data-testid="wizard-produccion-info">
                Definir producción · {wizard.n} componente{wizard.n === 1 ? "" : "s"} guardado{wizard.n === 1 ? "" : "s"} en esta línea
              </div>
              <Button type="button" variant="ghost" className="text-zinc-400" data-testid="wizard-mas-tarde"
                onClick={() => { wizard.n > 0 ? wizard.onTerminar(0) : wizard.onMasTarde(); handleClose(); }}>
                {wizard.n > 0 ? "Terminar sin añadir más" : "Definir producción más tarde"}
              </Button>
              <Button type="submit" variant="outline" disabled={saving || !form.linea_id || !form.nombre} data-testid="wizard-guardar-otro"
                onClick={() => setWizardAccion("otro")} className="bg-transparent border-amber-500/50 text-amber-200 hover:bg-amber-500/10">
                Guardar y añadir otro
              </Button>
              <Button type="submit" disabled={saving || !form.linea_id || !form.nombre} data-testid="form-comp-submit"
                onClick={() => setWizardAccion("terminar")} className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
                {saving ? "Guardando..." : "Guardar y terminar"}
              </Button>
            </DialogFooter>
          ) : (
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} className="bg-transparent border-zinc-700">Cancelar</Button>
            <Button type="submit"
              disabled={saving || (!isEdit && (!form.linea_id || !form.nombre))}
              data-testid="form-comp-submit"
              className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
              {saving ? "Guardando..." : (isEdit ? "Guardar cambios" : "Crear componente")}
            </Button>
          </DialogFooter>
          )}
        </form>
        {/* Mini-dialog: Crear material on-the-fly (Iter 60) */}
        <NuevoMaterialMiniDialog
          open={nuevoMatOpen}
          setOpen={setNuevoMatOpen}
          onCreated={async (newMat) => {
            if (onMaterialesChanged) {
              const updated = await onMaterialesChanged();
              // Auto-seleccionar el material recién creado
              const created = (updated || []).find((m) => m.id === newMat.id) || newMat;
              setForm((f) => {
                const factor = (created.unidad === "m²" && m2 && m2 > 0) ? m2 : 1;
                const mermaFactor = 1 + ((created.merma_pct || 0) / 100);
                return {
                  ...f,
                  material_id: created.id,
                  coste_unitario: +((created.precio_coste || 0) * factor * mermaFactor).toFixed(2),
                  precio_unitario: +((created.precio_venta || 0) * factor).toFixed(2),
                  proveedor_id: created.proveedor_id || f.proveedor_id,
                };
              });
            }
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

// ============ MINI-DIALOG: Nuevo material on-the-fly (Iter 60) ============
export function NuevoMaterialMiniDialog({ open, setOpen, onCreated }) {
  const [form, setForm] = useState({
    nombre: "", unidad: "m²", precio_coste: 0, precio_venta: 0, categoria: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ nombre: "", unidad: "m²", precio_coste: 0, precio_venta: 0, categoria: "" });
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!form.nombre.trim()) return;
    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre.trim(),
        unidad: form.unidad || "m²",
        precio_coste: parseFloat(form.precio_coste) || 0,
        precio_venta: parseFloat(form.precio_venta) || 0,
        categoria: form.categoria.trim() || null,
      };
      const { data } = await api.post("/materiales", payload);
      toast.success(`Material "${data.nombre}" creado`);
      setOpen(false);
      if (onCreated) await onCreated(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(`No se pudo crear el material: ${msg}`);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md">
        <DialogHeader>
          <DialogTitle>Nuevo material (rápido)</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Nombre *</Label>
            <Input value={form.nombre} autoFocus
              data-testid="nuevo-mat-nombre"
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="ej. Vinilo polimérico 3M IJ180" required
              className="bg-zinc-900 border-zinc-800" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Unidad</Label>
              <Select value={form.unidad} onValueChange={(v) => setForm({ ...form, unidad: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="nuevo-mat-unidad"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  {["m²", "ml", "ud", "kg", "l"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Categoría (opcional)</Label>
              <Input value={form.categoria}
                onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                placeholder="ej. Vinilo"
                className="bg-zinc-900 border-zinc-800" />
            </div>
            <div>
              <Label>Precio coste (€/{form.unidad || "ud"})</Label>
              <Input type="number" step="0.01" value={form.precio_coste}
                data-testid="nuevo-mat-precio-coste"
                onChange={(e) => setForm({ ...form, precio_coste: e.target.value })}
                className="bg-zinc-900 border-zinc-800" />
            </div>
            <div>
              <Label>Precio venta (€/{form.unidad || "ud"})</Label>
              <Input type="number" step="0.01" value={form.precio_venta}
                data-testid="nuevo-mat-precio-venta"
                onChange={(e) => setForm({ ...form, precio_venta: e.target.value })}
                className="bg-zinc-900 border-zinc-800" />
            </div>
          </div>
          <div className="text-[11px] text-zinc-500 leading-tight">
            Se creará en el catálogo global de Materiales. Puedes editar el resto de campos (proveedor, merma, stock…)
            después desde <span className="text-amber-300">Materiales</span>.
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="bg-transparent border-zinc-700">Cancelar</Button>
            <Button type="submit" disabled={saving || !form.nombre.trim()}
              data-testid="nuevo-mat-submit"
              className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
              {saving ? "Creando…" : "Crear y seleccionar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
